"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { EditSalonAppointmentFormSchema } from "./schema";
import { z } from "zod";
import { Form, FormLabel, FormMessage } from "@/components/ui/form";
import FormFields from "../Fields";
import Buttons from "@/components/core/Buttons";
import { UserJWTDecoded } from "@/types";
import hooks from "@/hooks";
import { useMemo } from "react";
import { DateTimePicker } from "@/components/ui/datetime-picker";
import { ptBR } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
import { IEditSalonAppointmentAction } from "@/actions/owner/types";

type SalonAppointment = z.infer<typeof EditSalonAppointmentFormSchema>;

interface IEditSalonAppointmentForm {
  user: UserJWTDecoded["user"];
  closeModal: (isOpen: boolean) => void;
  handleAfterEditAppointment: (
    oldApt: IEditSalonAppointmentAction["Response"],
    newApt: IEditSalonAppointmentAction["Response"],
  ) => void;
  apt: IEditSalonAppointmentAction["Response"];
}

const EditSalonAppointmentForm = ({
  user,
  closeModal,
  handleAfterEditAppointment,
  apt,
}: IEditSalonAppointmentForm) => {
  const t = useTranslations("Calendar");
  const { toast } = useToast();

  const form = useForm<SalonAppointment>({
    resolver: zodResolver(EditSalonAppointmentFormSchema),
    defaultValues: {
      datetime: new Date(apt.date),
      employee: apt.employee.id,
      service: apt.vocation.name,
    },
  });
  const datetimeValue = form.watch("datetime");
  const selectedEmployee = form.watch("employee");

  const {
    data: salonServices,
    isLoading: isLoadingSalonServices,
    isError: isErrorSalonServices,
  } = hooks.useGetSalonServicesByEmployeeId(selectedEmployee);

  const {
    data: salonEmployees,
    isLoading: isLoadingSalonEmployees,
    isError: isErrorSalonEmployees,
  } = hooks.useGetSalonEmployeesByOwnerId(user.userid);

  const salonAppointmentService = hooks.useEditSalonAppointment({
    onSuccess: (data) => {
      toast({
        title: "Sucesso",
        description: "Agendamento alterado com sucesso",
        variant: "success",
      });
      handleAfterEditAppointment(apt, data);
      closeModal(false);
    },
    onError: (error) => {
      toast({
        title: "Não foi possível editar o agendamento",
        description:
          error.message ||
          "O horário pode já estar ocupado ou houve um problema momentâneo. Tente novamente.",
        variant: "warning",
      });
    },
  });

  async function onSubmit(data: SalonAppointment) {
    await salonAppointmentService.mutateAsync({
      appointment_id: apt.id,
      date: data.datetime?.toISOString(),
      employee_id: data.employee,
      serviceName: data.service,
    });
  }

  const salonServicesOptions = useMemo(() => {
    if (isLoadingSalonServices || isErrorSalonServices) {
      return [];
    }
    if (!salonServices) return [];
    return salonServices.map((service) => ({
      label: service.name,
      value: service.name,
    }));
  }, [salonServices]);

  const salonEmployeeOptions = useMemo(() => {
    if (isLoadingSalonEmployees || isErrorSalonEmployees) {
      return [];
    }
    if (!salonEmployees) return [];
    return salonEmployees.map((service) => ({
      label: service.name,
      value: service.id,
    }));
  }, [salonEmployees]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="sm:p-6 md:p-8 w-full max-w-3xl mx-auto flex flex-col items-center gap-y-4 sm:gap-y-6"
      >
        <div className="w-full grid grid-cols-1 gap-4">
          <FormFields.SelectFormField
            form={form}
            formName="employee"
            label={t("appointment.editAppointmentModal.form.labels.employee")}
            placeholder={t(
              "appointment.editAppointmentModal.form.placeholder.employee",
            )}
            options={salonEmployeeOptions}
          />

          <FormFields.SelectFormField
            label={t("appointment.editAppointmentModal.form.labels.service")}
            form={form}
            formName="service"
            placeholder={t(
              "appointment.editAppointmentModal.form.placeholder.service",
            )}
            options={salonServicesOptions}
            isDisabled={!selectedEmployee}
          />

          {!selectedEmployee && (
            <FormMessage>
              Selecione um colaborador para visualizar os serviços disponíveis.
            </FormMessage>
          )}

          <div className="md:col-span-1">
            <FormLabel>
              {t("appointment.editAppointmentModal.form.labels.datetime")}
            </FormLabel>
            <DateTimePicker
              value={datetimeValue}
              placeholder={t(
                "appointment.editAppointmentModal.form.placeholder.datetime",
              )}
              onChange={(e) => form.setValue("datetime", e as Date)}
              locale={ptBR}
            />
            {form.formState.errors.datetime && (
              <FormMessage>
                {form.formState.errors.datetime.message}
              </FormMessage>
            )}
          </div>
        </div>
        <div className="w-full space-y-2 mt-2 sm:mt-4">
          <Buttons.DefaultButton
            className="py-4 sm:py-6 w-full"
            type="submit"
            title={t("appointment.editAppointmentModal.confirmButton")}
            isLoading={salonAppointmentService.isPending}
          />
          <Buttons.DefaultButton
            variant="secondary"
            className="py-4 sm:py-6 w-full"
            type="button"
            onClick={() => closeModal(false)}
            title={t("appointment.editAppointmentModal.closeButton")}
          />
        </div>
      </form>
    </Form>
  );
};

export default EditSalonAppointmentForm;
